// components/InCallQueueStack.tsx
import React, { useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { inCallNotifyBus, InCallQueueEvent } from 'utils/InCallNotifyBus';
import { InCallQueueChip } from './InCallQueueChip';

type StackProps = {
  enabled: boolean;
  inCall: boolean;
  handledIds?: string[];
  ttlMs?: number;
  maxVisible?: number;
  className?: string;
};

const Wrapper = styled.div`
  position: fixed;
  top: 16px;
  left: 16px;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  gap: 8px;
  pointer-events: none;

  /* Optional theming hook */
  ${({ theme }) =>
    theme?.inCallStack &&
    css`
      ${theme.inCallStack}
    `}
`;

const More = styled.div`
  background: rgba(18, 18, 18, 0.85);
  color: #fff;
  border-radius: 12px;
  padding: 8px 12px;
  font-size: 12px;
  pointer-events: none;
  width: 280px;
  text-align: center;
`;

const DEFAULT_TTL = 60_000;
const DEFAULT_MAX_VISIBLE = 3;

export const InCallQueueStack: React.FC<StackProps> = ({ enabled, inCall, handledIds, ttlMs = DEFAULT_TTL, maxVisible = DEFAULT_MAX_VISIBLE, className }) => {
  const [events, setEvents] = useState<InCallQueueEvent[]>([]);
  const timers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  useEffect(() => {
    const remove = inCallNotifyBus.subscribe(evt => {
      console.log('InCallQueueStack: Received event', evt);

      setEvents(prev => {
        if (prev.some(p => p.requestId === evt.requestId)) {
          console.log('InCallQueueStack: Duplicate event, ignoring', evt.requestId);
          return prev; // de-dup
        }
        console.log('InCallQueueStack: Adding new event', evt.requestId);
        return [evt, ...prev].slice(0, 6); // small buffer beyond visible
      });

      clearTimeout(timers.current[evt.requestId]);
      timers.current[evt.requestId] = setTimeout(() => {
        console.log('InCallQueueStack: Removing event after TTL', evt.requestId);
        setEvents(prev => prev.filter(p => p.requestId !== evt.requestId));
        delete timers.current[evt.requestId];
      }, ttlMs);
    });

    return () => {
      remove();
      Object.values(timers.current).forEach(clearTimeout);
      timers.current = {};
    };
  }, [ttlMs]);

  useEffect(() => {
    if (!handledIds?.length) return;
    console.log('InCallQueueStack: Filtering events by handledIds', handledIds);
    setEvents(prev => {
      const filtered = prev.filter(e => !handledIds.includes(e.requestId));
      console.log('InCallQueueStack: Events before filter', prev.length, 'after filter', filtered.length);
      return filtered;
    });
    handledIds.forEach(id => {
      clearTimeout(timers.current[id]);
      delete timers.current[id];
    });
  }, [handledIds]);

  if (!enabled || !inCall || events.length === 0) return null;

  const visible = events.slice(0, maxVisible);
  const extra = events.length - visible.length;

  return (
    <Wrapper className={className}>
      {visible.map(e => (
        <InCallQueueChip key={e.requestId} name={e.patientName} device={e.deviceName} emergency={e.isEmergency} />
      ))}
      {extra > 0 && <More>+{extra} more</More>}
    </Wrapper>
  );
};
